import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Checkbox, Drawer, Space, Typography, Tag, notification, Card, Divider, Popconfirm } from 'antd';
import { PlusOutlined, SettingOutlined, FolderOpenOutlined, SyncOutlined, DownloadOutlined, FileTextOutlined, CalculatorOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useBooks, bookLabel } from '../../lib/books';

import { label } from '../../lib/format';
import { CurrencySelect } from '../../components/CountrySelect';
const MONTHS = moment.months().map((m, i) => ({ value: i + 1, label: m }));
const money = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/** Settings of one set of books: currency, financial year, default posting accounts, ScanNRead tenant. */
function BooksSettingsDrawer({ book, onClose, onChanged, canEdit }) {
  const [form] = Form.useForm();
  const [scanForm] = Form.useForm();
  const [saving, setSaving] = React.useState(false);
  const [busy, setBusy] = React.useState(null);

  React.useEffect(() => {
    if (book) {
      form.setFieldsValue(book);
      scanForm.setFieldsValue({ scan_n_read_customer_id: book.scan_n_read_customer_id, scan_n_read_api_token: '' });
    }
  }, [book, form, scanForm]);

  const save = () =>
    form.validateFields().then((values) => {
      setSaving(true);
      request
        .put(`/ledgers/${book.id}`, values)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Books settings saved' });
            onChanged();
          } else notification.error({ message: r.message });
        })
        .finally(() => setSaving(false));
    });
  const connect = () =>
    scanForm.validateFields().then((values) => {
      setBusy('scan');
      request
        .put(`/ledgers/${book.id}/scan-n-read`, values)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: values.scan_n_read_customer_id ? 'ScanNRead tenant connected' : 'ScanNRead tenant disconnected' });
            onChanged();
          } else notification.error({ message: r.message });
        })
        .finally(() => setBusy(null));
    });
  const sync = () => {
    setBusy('sync');
    request
      .post(`/ledgers/${book.id}/sync`, {})
      .then((r) => r.json())
      .then((r) => (r.status ? notification.success({ message: `Synced: ${r.data.documentsScanned} documents, ${r.data.entriesCreated} created, ${r.data.entriesUpdated} updated, ${r.data.needsReview} need review` }) : notification.error({ message: r.message })))
      .finally(() => setBusy(null));
  };
  const reimport = () => {
    setBusy('import');
    request
      .post(`/ledgers/${book.id}/import-defaults`, {})
      .then((r) => r.json())
      .then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        const added = ['vatCodes', 'transactionCodes', 'vatFormLines'].reduce((s, k) => s + (r.data[k]?.created?.length || 0), 0) + (r.data.chartOfAccounts?.created?.length || 0);
        notification.success({ message: `${added} new template row(s) imported; existing rows kept.` });
      })
      .finally(() => setBusy(null));
  };

  return (
    <Drawer title={book ? `${bookLabel(book)} - settings` : ''} open={!!book} onClose={onClose} width={640}>
      {book && (
        <div>
          <Form form={form} layout="vertical" disabled={!canEdit}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input maxLength={150} /></Form.Item>
            <Space style={{ display: 'flex' }} align="start">
              <Form.Item name="financial_year_start_month" label="Financial year starts in" style={{ width: 220 }}><Select options={MONTHS} /></Form.Item>
              <Form.Item name="base_currency" label="Presentation currency" style={{ width: 220 }}><CurrencySelect /></Form.Item>
              <Form.Item name="status" label="Status" style={{ width: 160 }}><Select options={[{ value: 'ACTIVE', label: 'Active' }, { value: 'ARCHIVED', label: 'Archived' }]} /></Form.Item>
            </Space>
            <Divider orientation="left" plain>Default accounts for automatic postings (codes from this chart)</Divider>
            {!book.client_id && (
              <Form.Item name="gl_auto_post" valuePropName="checked"><Checkbox>Post the firm's invoices, receipts and credit notes to these books automatically</Checkbox></Form.Item>
            )}
            <Space style={{ display: 'flex' }} align="start" wrap>
              <Form.Item name="gl_debtors_account_code" label="Debtors (customers)" style={{ width: 150 }}><InputNumber style={{ width: '100%' }} placeholder="211" /></Form.Item>
              <Form.Item name="gl_creditors_account_code" label="Creditors (suppliers)" style={{ width: 150 }}><InputNumber style={{ width: '100%' }} placeholder="320" /></Form.Item>
              <Form.Item name="gl_bank_account_code" label="Bank" style={{ width: 130 }}><InputNumber style={{ width: '100%' }} placeholder="2240" /></Form.Item>
              <Form.Item name="gl_sales_account_code" label="Sales / fee income" style={{ width: 150 }}><InputNumber style={{ width: '100%' }} placeholder="5025" /></Form.Item>
              <Form.Item name="gl_depreciation_expense_account_code" label="Depreciation expense" style={{ width: 160 }}><InputNumber style={{ width: '100%' }} placeholder="7060" /></Form.Item>
            </Space>
            <Space style={{ display: 'flex' }} align="start" wrap>
              <Form.Item name="gl_disposal_account_code" label="Gain / loss on disposal" style={{ width: 170 }}><InputNumber style={{ width: '100%' }} placeholder="5210" /></Form.Item>
              <Form.Item name="gl_pl_transfer_account_code" label="Year-end transfer (P&L side)" style={{ width: 200 }}><InputNumber style={{ width: '100%' }} placeholder="9999" /></Form.Item>
              <Form.Item name="gl_retained_earnings_account_code" label="Retained earnings" style={{ width: 160 }}><InputNumber style={{ width: '100%' }} placeholder="4095" /></Form.Item>
            </Space>
            <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
            {canEdit && <Button type="primary" onClick={save} loading={saving}>Save settings</Button>}
          </Form>

          <Divider orientation="left" plain>ScanNRead (scanned-document intake)</Divider>
          <Typography.Paragraph type="secondary">
            The ScanNRead tenant whose scanned invoices and receipts feed these books. {book.has_scan_n_read ? `Connected (customer id ${book.scan_n_read_customer_id}).` : 'Not connected yet.'}
          </Typography.Paragraph>
          <Form form={scanForm} layout="vertical" disabled={!canEdit}>
            <Space style={{ display: 'flex' }} align="start">
              <Form.Item name="scan_n_read_customer_id" label="ScanNRead customer id" style={{ width: 200 }}><InputNumber style={{ width: '100%' }} /></Form.Item>
              <Form.Item name="scan_n_read_api_token" label="Auth-APIToken" style={{ width: 340 }}><Input.Password placeholder={book.has_scan_n_read ? '(unchanged unless typed)' : ''} /></Form.Item>
            </Space>
            {canEdit && (
              <Space>
                <Button onClick={connect} loading={busy === 'scan'}>Save connection</Button>
                <Button icon={<SyncOutlined />} onClick={sync} loading={busy === 'sync'} disabled={!book.has_scan_n_read}>Sync VAT ledger now</Button>
              </Space>
            )}
          </Form>

          <Divider orientation="left" plain>Template</Divider>
          <Typography.Paragraph type="secondary">Pull in anything added to the ADMIN template since these books were opened (new accounts, VAT codes, form lines). Existing rows are never changed.</Typography.Paragraph>
          {canEdit && <Button icon={<DownloadOutlined />} onClick={reimport} loading={busy === 'import'}>Re-import template rows</Button>}
        </div>
      )}
    </Drawer>
  );
}

/** Client Books (xlsx MENU "Accounting -> Select a Client"): one set of books per client the firm keeps books for, plus the firm's own. */
export default function BooksPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { books, reload, loading, setLedgerId, companyId, companies, setCompanyId, isSystemAdmin } = useBooks();
  const canEdit = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const [candidates, setCandidates] = React.useState([]);
  const [openModal, setOpenModal] = React.useState(false);
  const [opening, setOpening] = React.useState(false);
  const [settings, setSettings] = React.useState(null);
  const [form] = Form.useForm();

  const loadCandidates = React.useCallback(() => {
    if (!companyId) return;
    request.get('/ledgers/candidates', { company_id: companyId }).then((r) => r.json()).then((r) => r.status && setCandidates(r.data));
  }, [companyId]);
  React.useEffect(loadCandidates, [loadCandidates]);

  const openBooks = () =>
    form.validateFields().then((values) => {
      setOpening(true);
      request
        .post('/ledgers', { ...values, company_id: companyId })
        .then((r) => r.json())
        .then((r) => {
          if (!r.status) return notification.error({ message: r.message });
          notification.success({ message: r.message });
          setOpenModal(false);
          reload().then(() => setLedgerId(r.data.id));
          loadCandidates();
        })
        .finally(() => setOpening(false));
    });

  const goTo = (book, path) => {
    setLedgerId(book.id);
    navigate(`${path}${path.includes('?') ? '&' : '?'}ledger=${book.id}`);
  };
  const refreshSettings = () => reload().then((list) => setSettings((s) => (s ? list.find((b) => b.id === s.id) || null : null)));

  const columns = [
    { title: 'Books', key: 'name', width: 240, render: (_, b) => <Typography.Link onClick={() => goTo(b, '/journal')}>{bookLabel(b)}</Typography.Link> },
    { title: 'Type', key: 'type', width: 110, render: (_, b) => (b.client_id ? <Tag color="blue">Client</Tag> : <Tag>Own books</Tag>) },
    { title: 'Client', key: 'client', width: 200, render: (_, b) => (b.Client ? <Link to={`/clients/${b.client_id}`}>{b.Client.client_name}</Link> : <Typography.Text type="secondary">The firm itself</Typography.Text>) },
    { title: 'Currency', dataIndex: 'base_currency', width: 90 },
    { title: 'FY starts', dataIndex: 'financial_year_start_month', width: 110, render: (v) => moment.months()[v - 1] },
    { title: 'Transactions', dataIndex: 'entries', align: 'right', width: 110 },
    { title: 'Last posting', dataIndex: 'last_entry_date', width: 110, render: (v) => v || '-' },
    { title: 'ScanNRead', dataIndex: 'has_scan_n_read', width: 100, render: (v) => (v ? <Tag color="green">connected</Tag> : <Typography.Text type="secondary">-</Typography.Text>) },
    { title: 'Status', dataIndex: 'status', width: 90, render: (v) => <Tag color={v === 'ACTIVE' ? 'green' : 'default'}>{label(v)}</Tag> },
    {
      title: '',
      key: 'actions',
      width: 300,
      render: (_, b) => (
        <Space size={4} wrap>
          <Button size="small" icon={<FolderOpenOutlined />} onClick={() => goTo(b, '/journal')}>Posting</Button>
          <Button size="small" icon={<CalculatorOutlined />} onClick={() => goTo(b, '/ledger-reports?tab=tb')}>Reports</Button>
          <Button size="small" icon={<FileTextOutlined />} onClick={() => goTo(b, '/vat-reports')}>VAT</Button>
          <Button size="small" icon={<SettingOutlined />} onClick={() => setSettings(b)}>Settings</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Client Books
          </Typography.Title>
          <Typography.Text type="secondary">
            Every client the firm keeps books for has its own chart of accounts, transaction and VAT codes, ledger, VAT returns and asset register. Choose the books once; every accounting screen then works on them.
          </Typography.Text>
        </div>
        <Space>
          {isSystemAdmin && <Select style={{ width: 180 }} value={companyId} onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />}
          {canEdit && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ financial_year_start_month: 1, base_currency: 'EUR' }); setOpenModal(true); }}>
              Open books for a client
            </Button>
          )}
        </Space>
      </div>

      <Table rowKey="id" size="small" columns={columns} dataSource={books} loading={loading} pagination={false} scroll={{ x: 1200 }} />

      {candidates.length > 0 && (
        <Card size="small" style={{ marginTop: 16 }} title={`Clients without books (${candidates.length})`}>
          <Space wrap>
            {candidates.map((c) => (
              <Popconfirm key={c.id} title={`Open books for ${c.client_name} with the standard chart and VAT setup?`} disabled={!canEdit} onConfirm={() => { form.setFieldsValue({ client_id: c.id, financial_year_start_month: 1, base_currency: 'EUR' }); openBooks(); }}>
                <Tag style={{ cursor: canEdit ? 'pointer' : 'default' }}>{c.client_name}{c.client_code ? ` (${c.client_code})` : ''}</Tag>
              </Popconfirm>
            ))}
          </Space>
        </Card>
      )}

      <Modal title="Open books for a client" open={openModal} onCancel={() => setOpenModal(false)} onOk={openBooks} okText="Open books" confirmLoading={opening}>
        <Typography.Paragraph type="secondary">The client's books start with the ADMIN template: the full chart of accounts, transaction codes, Cyprus VAT codes and VAT form lines.</Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item name="client_id" label="Client" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={candidates.map((c) => ({ value: c.id, label: `${c.client_name}${c.client_code ? ` (${c.client_code})` : ''}` }))} placeholder="Clients without books" />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="financial_year_start_month" label="Financial year starts in" style={{ width: 240 }}><Select options={MONTHS} /></Form.Item>
            <Form.Item name="base_currency" label="Presentation currency" style={{ width: 220 }}><CurrencySelect /></Form.Item>
          </Space>
        </Form>
      </Modal>

      <BooksSettingsDrawer book={settings} onClose={() => setSettings(null)} onChanged={refreshSettings} canEdit={canEdit} />
    </div>
  );
}

export { money };
